/**
 * Copy and option data for the outcome-capture flow (MRG-926).
 *
 * Values come from the FINAL Claude Design files. See
 * `openspec/changes/add-manual-outcome-capture/design-reference.md` for the
 * extraction and for the two places the desktop and mobile files disagree
 * (desktop wins — it is newer and carries the Under Contract flow).
 */

// =============================================================================
// Wire values — these must match the API's enums exactly
// =============================================================================

export type OutcomeTargetStatus = 'sold' | 'removed' | 'under_contract'

export type WinnerKind = 'offer' | 'inquiry' | 'free_text_investor' | 'outside'

/**
 * Outside-Orchard-Offers outcome reasons.
 *
 * Ids are the design's, verbatim. They are NOT interchangeable synonyms —
 * `traditional` means "sold on the open market" and `other-cash` means "sold to
 * a cash buyer outside Orchard Offers". Getting these crossed would silently
 * mis-file every outcome in reporting.
 */
export type OutsideReason =
  | 'other-cash'
  | 'partner-outside'
  | 'traditional'
  | 'mls-listing'
  | 'retail-buyer'
  | 'not-selling'
  | 'listing-terminated'
  | 'other-text'

export type OutsidePartner =
  | 'opendoor'
  | 'offerpad'
  | 'homeward'
  | 'homevestors'
  | 'homego'
  | 'other-partner'

export type WouldWorkAgain = 'no' | 'maybe' | 'yes'

// =============================================================================
// Options
// =============================================================================

export interface OutcomeOption {
  /** Wire value, or a sentinel handled by the reducer. */
  value: string
  label: string
  /** Lighter supporting line beneath the label. */
  sub?: string
}

/**
 * Cash-offer partners outside Orchard Offers.
 *
 * Hardcoded per product decision — these change rarely and an admin CRUD
 * surface would be more machinery than the data warrants.
 */
export const OUTSIDE_PARTNERS: ReadonlyArray<{ value: OutsidePartner; label: string }> = [
  { value: 'opendoor', label: 'Opendoor' },
  { value: 'offerpad', label: 'Offerpad' },
  { value: 'homeward', label: 'Homeward' },
  { value: 'homevestors', label: 'HomeVestors' },
  { value: 'homego', label: 'HomeGo' },
  { value: 'other-partner', label: 'Other' },
]

/** value -> label, for naming a partner carried out of a stored capture. */
export const OUTSIDE_PARTNER_LABELS: Record<OutsidePartner, string> = OUTSIDE_PARTNERS.reduce(
  (acc, partner) => ({ ...acc, [partner.value]: partner.label }),
  {} as Record<OutsidePartner, string>
)

/**
 * Sentinels for "the buyer came through Orchard Offers but has no Offer row".
 * The Sold and Removed flows use different ids for the same idea.
 */
export const OO_SOURCED = 'oo-sourced' as const
export const OO_INVESTOR = 'oo-investor' as const
export const FREE_TEXT_INVESTOR_OPTIONS: readonly string[] = [OO_SOURCED, OO_INVESTOR]

/** Options that reveal the partner dropdown. */
export const PARTNER_PICKER_OPTIONS: readonly string[] = ['other-cash', 'partner-outside']

/** The free-text "Other" option. */
export const OTHER_TEXT = 'other-text' as const

/** Sentinel for "none of the listed offers worked out". */
export const NONE_OF_THESE = 'none-of-these' as const

/**
 * m1e — how a property sold.
 *
 * Four options, in the design's order. There is deliberately no "No longer for
 * sale" here: that answer belongs to the Removed flow, and a property cannot
 * both have sold and not be for sale.
 */
export const SOLD_OPTIONS: readonly OutcomeOption[] = [
  {
    value: 'other-cash',
    label: 'Sold to a cash buyer outside of Orchard Offers',
  },
  {
    value: 'traditional',
    label: 'Sold on the open market',
  },
  {
    value: OO_SOURCED,
    label: 'I found my investor on Orchard Offers',
  },
  { value: OTHER_TEXT, label: 'Other' },
]

/**
 * m2a — the narrowed follow-up after the agent rules out the Orchard Offers
 * offers on the property.
 *
 * Three options and NO sub-labels, unlike m1e. The agent has already been shown
 * the explanatory version; this is the short form.
 */
export const SOLD_FALLBACK_OPTIONS: readonly OutcomeOption[] = [
  { value: 'other-cash', label: 'Sold to a cash buyer outside of Orchard Offers' },
  { value: 'traditional', label: 'Sold on the open market' },
  { value: OTHER_TEXT, label: 'Other' },
]

/**
 * r1a — what happened to a removed property.
 *
 * "Decided not to sell" appears only here, never in the Sold flow. That
 * asymmetry is intentional: Sold implies a transaction happened.
 */
export const REMOVED_OPTIONS: readonly OutcomeOption[] = [
  {
    value: 'not-selling',
    label: 'Decided not to sell',
  },
  {
    value: OO_INVESTOR,
    label: 'Sold to an investor on Orchard Offers',
  },
  {
    value: 'partner-outside',
    label: 'Sold to a cash buyer outside of Orchard Offers',
  },
  {
    value: 'mls-listing',
    label: 'Listing on the MLS',
  },
  {
    // A departure in its own right — the seller ended the relationship — not
    // a question about where the house went afterwards. Without it, the most
    // common way an agent loses a listing had to be filed as "Other".
    value: 'listing-terminated',
    label: 'Listing agreement terminated',
  },
  { value: OTHER_TEXT, label: 'Other' },
]

/**
 * r1c — shown after the agent says none of the surfaced offers worked out.
 *
 * No Orchard-Offers-investor option here: they have just told us none of the
 * Orchard Offers connections panned out, so offering it again would be
 * contradictory.
 */
export const REMOVED_FALLBACK_OPTIONS: readonly OutcomeOption[] = REMOVED_OPTIONS.filter(
  (option) => option.value !== OO_INVESTOR
)

/**
 * u1e — who the agent is moving forward with.
 *
 * Forward-looking rather than past-tense, and with no "not for sale" option:
 * Under Contract implies an active deal.
 */
export const UNDER_CONTRACT_OPTIONS: readonly OutcomeOption[] = [
  { value: OO_SOURCED, label: 'An investor I found on Orchard Offers' },
  { value: 'other-cash', label: 'A cash buyer outside of Orchard Offers' },
  { value: 'retail-buyer', label: 'A retail buyer on the open market' },
  { value: OTHER_TEXT, label: 'Other' },
]

/**
 * u2a — shown after the agent rules out the offers on the property.
 *
 * No Orchard Offers option: they have just told us none of the Orchard Offers
 * connections are the one they are moving forward with.
 */
export const UNDER_CONTRACT_FALLBACK_OPTIONS: readonly OutcomeOption[] =
  UNDER_CONTRACT_OPTIONS.filter((option) => option.value !== OO_SOURCED)

// =============================================================================
// Copy
// =============================================================================

export const COPY = {
  confirmBuyerBody:
    'Confirming the buyer gives them credit for the deal and lets other investors know the property is no longer available.',
  /**
   * Same screen, but confirming a buyer carried from Under Contract.
   *
   * Deliberately drops "gives them credit for the deal": that buyer is not on
   * Orchard Offers, so there is no credit to give them here.
   */
  confirmBuyerBodyCarried:
    'Confirming the buyer lets other investors know the property is no longer available.',
  confirmBuyerBodyUnderContract:
    'Confirming the buyer matches them to the deal and lets other investors know the property is under contract.',

  /**
   * d1p body (Deal Hub sync). Deliberately shorter than `confirmBuyerBody`:
   * a cash offer partner gets no credit for an Orchard Offers connection, so
   * the only thing confirming does is release the investors still waiting.
   */
  confirmPartnerBody:
    'Confirming lets other investors know the property is no longer available.',
  /**
   * d1p title.
   *
   * "through", not "to": the agent's experience of a cash offer partner is a
   * route the sale went down, not a person they negotiated with. The stored
   * outcome still credits the partner as the buyer — this is how the question
   * reads best to an agent, not a claim about who holds the deed.
   */
  confirmPartnerTitle: (address: string, partner: string) =>
    `Did you sell ${address} through ${partner}?`,
  cashOfferPartnerBadge: 'Cash offer partner',
  /**
   * Rule 2's second name on the m1a subject card.
   *
   * Not worded in the design file, which specifies only "both names in the
   * subject card". Labelled rather than stacked bare, so the agent can tell
   * which side each name came from.
   */
  dealHubBuyerLine: (name: string) => `Deal Hub buyer: ${name}`,

  /**
   * m1a title.
   *
   * Names the property as well as the buyer whenever the address is known. The
   * agent may not have set this status themselves — the Deal Hub sync can drop
   * them straight onto this screen — so the question has to say which listing
   * it is about rather than assuming they are already looking at it.
   */
  confirmBuyerTitle: (investor: string, address?: string) => {
    // Company names often end in a period ("Steadfast Property Co."), which the
    // question mark would double up into "Co.?". Same trim the completion toast
    // already does.
    const named = investor.replace(/\.+$/, '')
    return address ? `Did you close ${address} with ${named}?` : `Did you close with ${named}?`
  },

  pickBuyerTitle: "Who'd you close with?",
  pickBuyerTitleUnderContract: 'Who are you moving forward with?',
  // The escape row at the bottom of a candidate list. Removed asks whether any
  // of the offers worked out, so its escape is a "no"; Sold and Under Contract
  // ask who the buyer is, so theirs is just another answer.
  noneOfThese: 'No — none of these worked out',
  noneOfTheseOther: 'Other',

  soldTitle: (address: string) => `How did ${address} sell?`,
  removedTitle: (address?: string) =>
    address ? `What happened to ${address}?` : 'What happened to this property?',
  /**
   * Neutral context line on the Removed screens.
   *
   * The Sold flow gets this for free from its 🎉 line; Removed has no
   * celebration, so without it the agent is asked "What happened to this
   * property?" with no way to tell which property, or when. The sync can raise
   * this weeks after the fact, so the date is doing real work.
   */
  removedContext: (address: string, removedOn?: string) =>
    removedOn
      ? `Deal Hub marked ${address} removed from the marketplace on ${removedOn}.`
      : `Deal Hub marked ${address} removed from the marketplace.`,
  /**
   * r1b title.
   *
   * Names the property for the same reason the Removed reason list does: the
   * Deal Hub sync can drop an agent onto this screen for a listing they have
   * not thought about in weeks, and "these" is only answerable once you know
   * which property's offers you are looking at.
   */
  removedListTitle: (address?: string) =>
    address ? `Did any of these work out for ${address}?` : 'Did any of these work out?',

  partnerTitle: 'Which buyer?',
  partnerPlaceholder: 'Choose a partner',
  otherTextLabel: 'What happened? (optional)',
  investorNameLabel: 'Which investor?',
  investorNamePlaceholder: 'Company name',

  contractTitle: (investor: string) => `Upload the contract for ${investor}'s offer`,
  contractBody:
    "We'll use it to confirm the deal and track connections made on Orchard Offers. It won't be shared with anyone outside of Orchard.",
  /**
   * When a contract is already on file for the winning offer the step stops
   * asking and starts confirming — the agent has nothing to do unless the
   * contract has changed.
   */
  contractTitleExisting: (investor: string) => `Confirm the contract for ${investor}'s offer`,
  contractBodyExisting:
    "We already have a contract on file for this offer. If it's changed, upload the new version.",
  /**
   * Control-agnostic on purpose: the affordance beneath is a filled button on
   * the standard step and a link once a contract is already on file, so naming
   * it would be wrong on one of the two.
   */
  dropzoneHint:
    'Drag and drop a file, or choose one below. PDF, DOC, DOCX, PNG, JPG · max 25 MB.',
  chooseFile: 'Choose file',
  replaceFile: 'Replace file',
  uploaded: 'Uploaded',

  wouldWorkAgainTitle: (investor: string) => `Would you work with ${investor} again?`,
  investorFeedbackDisclaimer:
    "Your answers, combined with other agents' feedback, help agents know what to expect from each investor and help us improve the marketplace. Your name, comments, and deal details always stay private.",
  wouldWorkAgainPlaceholder: 'Closed early, easy to reach all week.',
  wouldWorkAgainNoteLabel: 'Anything worth noting? (optional)',

  ratingTitle: 'How was your experience with Orchard Offers?',
  ratingHint: 'Tap a star to rate',
  feedbackDisclaimer:
    "Your feedback will help us improve Orchard Offers in the future. It won't be shared with anyone outside of Orchard.",
  ratingNoteLabel: 'What would have made the difference? (optional)',
  ratingNotePlaceholder: "Offers came in too low, buyers went quiet, timing didn't work…",

  // Confirmation on m3a / m3b. Three separate parts in the design — a success
  // badge, a heading, and a body sentence chosen by outcome path. Verbatim.
  confirmBadge: 'Outcome recorded',
  /**
   * Accent line above the title on m1a / u1a.
   *
   * Takes the seller's first name only. `properties.client` holds a full name
   * and sometimes two ("Melinda and Matthew Mallay", "JOSH AND MEGAN ADKINS"),
   * so the design's "for the {client}" would read as "for the Jane Smith".
   * Product decision: first name, no article — do not assume a family.
   */
  cheer: (address: string, sellerFirstName: string, closingDate?: string) =>
    closingDate
      ? `🎉 Congrats on getting ${address} across the finish line for ${sellerFirstName} on ${closingDate}.`
      : `🎉 Congrats on getting ${address} across the finish line for ${sellerFirstName}.`,
  /**
   * Completion toast. Deliberately states only the status change and the matched
   * investor — no mention of offers closed out.
   */
  toastWithInvestor: (address: string, verb: string, company: string) =>
    // Company names often end in a period ("Steadfast Property Co."), which
    // the sentence's own full stop would double up.
    `${address} is marked ${verb} with ${company.replace(/\.+$/, '')}.`,
  toastWithoutInvestor: (address: string, verb: string) =>
    `${address} is marked ${verb}.`,
  confirmTitle: (address: string) => `${address} is all set`,
  /** Orchard Offers investor named directly. */
  confirmBodyCredited: (company: string, verb: string) =>
    `We've credited ${company} with the deal and marked the listing ${verb}.`,
  /** Orchard Offers investor whose existing offer was accepted. */
  confirmBodyAccepted: (company: string, others: number) =>
    `We've accepted ${company}'s offer and closed out the other ${others} offers and inquiries.`,
  /** Cash buyer outside Orchard Offers. */
  confirmBodyOutsideCash: (total: number) =>
    `We've closed the ${total} pending investor offers and inquiries for you.`,
  /** Sold on the open market / listed on the MLS. */
  confirmBodyMls: (total: number) =>
    total
      ? `We've withdrawn it from Orchard Offers and closed out the ${total} offers and inquiries for you.`
      : "We've withdrawn it from Orchard Offers.",
  /** No longer for sale / decided not to sell. */
  confirmBodyWithdrawn:
    "We've withdrawn the listing from Orchard Offers and let the investors waiting on it know.",
  /** Free-text "something else". */
  confirmBodyOther: (total: number) =>
    `Thanks — we've recorded what you told us and closed the ${total} pending offers and inquiries.`,

  offeredDetail: (amount: string, emd: string, timeline: string) =>
    `Offered ${amount} · ${emd} EMD · ${timeline}`,
  acceptedBadge: 'Accepted',
  offersLabel: 'Offers',
  inquiriesLabel: 'Inquiries',
  lockedMessage:
    "This property's outcome is recorded. Status and offer statuses are locked.",
} as const

/**
 * Three equal-weight pills, negative → affirmative left to right, matching the
 * convention used by the 5-star rating elsewhere in the flow.
 */
export const WOULD_WORK_AGAIN_OPTIONS: ReadonlyArray<{ value: WouldWorkAgain; label: string }> = [
  { value: 'no', label: 'No' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'yes', label: 'Yes' },
]

/** Minimum touch target, per the MobilePropertiesToolbar precedent. */
export const TOUCH_TARGET_PX = 44
